import { useCallback, useState } from "react"
import { Alert, Box, Chip, CircularProgress, Divider, Switch, Tab, Tabs, Typography } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import { ERROR, OK } from "helpers/data-statuses"
import { useAnalytics } from "connected-components/analytics"
import { SKILL_EVENTS } from "connected-components/skills/analytics/events"
import type { SkillCatalogSkill } from "connected-components/skills/types"
import { useSkillDownload } from "connected-components/skills/hooks/use-skill-download"
import { useSkillFiles } from "resources/skills"
import { SkillDownloadButton } from "./skill-download-button"
import { SkillFileTree } from "./skill-file-tree"
import { SkillFileViewer } from "./skill-file-viewer"
import { formatSkillMeta, tierLabel } from "./skill-meta"
import type { SkillsPageLayout } from "./skills-page-content"

const DETAIL_SX = { display: "flex", flexDirection: "column", gap: 2, minWidth: 0 } satisfies SxProps<Theme>
const HEADER_SX = { display: "flex", alignItems: "flex-start", gap: 2, flexWrap: "wrap" } satisfies SxProps<Theme>
const HEADING_BLOCK_SX = { display: "flex", flexDirection: "column", gap: 0.5, flex: 1, minWidth: 240 } satisfies SxProps<Theme>
const TITLE_SX = { display: "flex", alignItems: "baseline", gap: 1.5, flexWrap: "wrap", m: 0 } satisfies SxProps<Theme>
const PILL_ROW_SX = { display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.75 } satisfies SxProps<Theme>
// The tier is where the skill came from, not one of its tags, so it leads the row and carries a filled
// background the outlined category chips beside it do not. color-mix on --app-primary is scheme-aware.
const TIER_CHIP_SX = { bgcolor: "color-mix(in srgb, var(--app-primary) 14%, transparent)", color: "var(--app-text-primary)", fontWeight: 600 } satisfies SxProps<Theme>
const META_SX = { fontSize: "0.8125rem", color: "text.secondary" } satisfies SxProps<Theme>
const DESCRIPTION_SX = { color: "text.primary", mt: 1 } satisfies SxProps<Theme>
const SWITCH_ROW_SX = { display: "flex", alignItems: "center", gap: 1, ml: "auto" } satisfies SxProps<Theme>
const SWITCH_LABEL_SX = { fontSize: "0.875rem", fontWeight: 500, color: "text.secondary" } satisfies SxProps<Theme>
const FILES_ONE_COLUMN_SX = { display: "flex", flexDirection: "column", gap: 2 } satisfies SxProps<Theme>
const FILES_TWO_COLUMN_SX = { display: "grid", gridTemplateColumns: "minmax(180px, 1fr) minmax(0, 3fr)", gap: 3, alignItems: "start" } satisfies SxProps<Theme>
const NOTICE_SX = { color: "text.secondary", py: 1.5 } satisfies SxProps<Theme>
const LOADING_SX = { display: "flex", justifyContent: "center", p: 2 } satisfies SxProps<Theme>

const PANEL_SX = { display: "flex", flexDirection: "column", gap: 2, minWidth: 0 } satisfies SxProps<Theme>

const DEFAULT_FILE = "SKILL.md"
const FILES_TAB_ID = "kelly-skill-files-tab"
const FILES_PANEL_ID = "kelly-skill-files-panel"

export interface SkillDetailProps {
  skill: SkillCatalogSkill
  layout: SkillsPageLayout
  enabled: boolean
  isSaving: boolean
  onToggle: (skill: SkillCatalogSkill, enabled: boolean) => void
}

export function SkillDetail({ skill, layout, enabled, isSaving, onToggle }: SkillDetailProps) {
  const { status, files } = useSkillFiles(skill.tier, skill.id)
  const { downloadSkill, isDownloading } = useSkillDownload(skill)
  const analytics = useAnalytics()
  const [selectedPath, setSelectedPath] = useState<string | null>(null)
  const defaultPath = files.find((file) => file.path === DEFAULT_FILE)?.path ?? files[0]?.path ?? null
  const activePath = selectedPath ?? defaultPath
  const activeFile = files.find((file) => file.path === activePath) ?? null

  const onSelectFile = useCallback((path: string) => {
    analytics.track(SKILL_EVENTS.SKILL_FILE_VIEWED, { skillId: skill.id, path })
    setSelectedPath(path)
  }, [analytics, skill.id])

  return (
    <Box sx={DETAIL_SX} data-testid="kelly-skill-detail">
      <Box sx={HEADER_SX}>
        <Box sx={HEADING_BLOCK_SX}>
          <Typography component="h3" variant="h6" sx={TITLE_SX}>
            {skill.name}
            {skill.beta && <Chip size="small" label="Preview" />}
          </Typography>
          <Typography component="span" sx={META_SX} data-testid="kelly-skill-detail-meta">{formatSkillMeta(skill, { withAuthor: true })}</Typography>
          <Box sx={PILL_ROW_SX}>
            <Chip size="small" label={tierLabel(skill.tier)} sx={TIER_CHIP_SX} data-testid="kelly-skill-tier" />
            {skill.categories?.map((category) => (
              <Chip key={category} size="small" variant="outlined" label={category} data-testid="kelly-skill-category" />
            ))}
          </Box>
          <Typography variant="body1" sx={DESCRIPTION_SX}>{skill.description}</Typography>
        </Box>
        <Box sx={SWITCH_ROW_SX}>
          <SkillDownloadButton skillName={skill.name} isDownloading={isDownloading} onDownload={downloadSkill} />
          <Switch
            color="primary"
            checked={enabled}
            disabled={isSaving}
            onChange={(_, checked) => onToggle(skill, checked)}
            slotProps={{ input: { "aria-label": `Use ${skill.name}` } }}
          />
          <Typography component="span" sx={SWITCH_LABEL_SX}>{enabled ? "Enabled" : "Disabled"}</Typography>
        </Box>
      </Box>
      <Divider />
      <Tabs value="files" aria-label="Skill detail">
        <Tab value="files" label="Files" id={FILES_TAB_ID} aria-controls={FILES_PANEL_ID} />
      </Tabs>
      <Box role="tabpanel" id={FILES_PANEL_ID} aria-labelledby={FILES_TAB_ID} sx={PANEL_SX}>
        {status === ERROR && <Alert severity="error">The files for {skill.name} did not load. Try again later.</Alert>}
        {status !== ERROR && status !== OK && <Box sx={LOADING_SX}><CircularProgress size={20} aria-label="Loading files" /></Box>}
        {status === OK && files.length === 0 && (
          <Typography sx={NOTICE_SX} data-testid="kelly-skill-files-empty">This skill&apos;s bundle has no files to show.</Typography>
        )}
        {status === OK && files.length > 0 && (
          <Box sx={layout === "two-column" ? FILES_TWO_COLUMN_SX : FILES_ONE_COLUMN_SX}>
            <SkillFileTree files={files} selectedPath={activePath} onSelect={onSelectFile} />
            {activeFile && <SkillFileViewer key={activeFile.path} tier={skill.tier} skillId={skill.id} file={activeFile} />}
          </Box>
        )}
      </Box>
    </Box>
  )
}
